// Frontend mirror of src/bds/modules/permissions/permissions.constants.ts.
//
// Used as a fail-safe by AuthContext: if /auth/me/capabilities is briefly
// unreachable (network blip, transient 5xx, deploy in flight), we fall
// back to the role's default capability set so the UI keeps working
// instead of locking the user out of every <RequireCapability> route.
//
// The authoritative source is the backend constants file; if that file
// changes, mirror the change here. Both lists are typed as readonly
// strings so a missing entry is caught at build time by `tsc -b`.

import { ViewType } from '@/types/domain';

export const PERMISSION_CAPABILITIES = [
  'dashboard.view',
  'tickets.view',
  'tickets.create',
  'tickets.update_status',
  'tickets.log_cx',
  'tickets.add_note',
  'tickets.handoff',
  'tickets.close',
  'users.create',
  'users.update',
  'users.reset_pin',
  'users.delete',
  'tickets.reassign',
  'tickets.approve',
  'tickets.delete',
  'tickets.archive',
  'tickets.restore',
  'tickets.view_archived',
  'tickets.view_deleted',
  'approvals.view',
  'my_queue.view',
  'users.view',
  'shopify.view',
  'shopify.manage',
  'permissions.manage',
  'reports.view',
  'notifications.view',
  'notifications.manage',
  'auth.change_pin',
  'attachments.upload',
  'attachments.download'
] as const;

export type PermissionCapability = (typeof PERMISSION_CAPABILITIES)[number];

export const ROLE_DEFAULT_CAPABILITIES: Record<ViewType, PermissionCapability[]> = {
  [ViewType.Admin]: [...PERMISSION_CAPABILITIES],
  [ViewType.Specialist]: [
    // V4.9.22 — Specialists (the ticket "solvers") get Reports by default.
    // Mirrors the backend constant; keeps the pre-fetch seed accurate so the
    // Reports nav/route shows immediately (before /auth/me/capabilities resolves).
    'reports.view',
    'dashboard.view',
    'tickets.view',
    'tickets.create',
    'tickets.update_status',
    'tickets.log_cx',
    'tickets.add_note',
    'tickets.handoff',
    'tickets.close',
    'tickets.reassign',
    'tickets.archive',
    'tickets.restore',
    'my_queue.view',
    'shopify.view',
    'attachments.upload',
    'attachments.download',
    'auth.change_pin'
  ],
  [ViewType.Communicator]: [
    'dashboard.view',
    'tickets.view',
    'tickets.create',
    'tickets.update_status',
    'tickets.log_cx',
    'tickets.add_note',
    'tickets.handoff',
    'tickets.close',
    'tickets.reassign',
    'tickets.archive',
    'tickets.restore',
    'my_queue.view',
    'attachments.upload',
    'attachments.download',
    'auth.change_pin'
  ],
  [ViewType.SalesLead]: [
    'dashboard.view',
    'tickets.view',
    'tickets.create',
    'tickets.update_status',
    'tickets.log_cx',
    'tickets.add_note',
    'tickets.handoff',
    'tickets.close',
    'tickets.reassign',
    'tickets.archive',
    'tickets.restore',
    'my_queue.view',
    'shopify.view',
    'attachments.upload',
    'attachments.download',
    'auth.change_pin'
  ],
  [ViewType.Agent]: [
    'dashboard.view',
    'tickets.view',
    'tickets.create',
    'tickets.update_status',
    'tickets.log_cx',
    'tickets.add_note',
    'tickets.handoff',
    'tickets.close',
    'tickets.reassign',
    'tickets.archive',
    'tickets.restore',
    'my_queue.view',
    'attachments.upload',
    'attachments.download',
    'auth.change_pin'
  ]
};

export function defaultCapabilitiesFor(viewType: ViewType): Set<string> {
  return new Set(ROLE_DEFAULT_CAPABILITIES[viewType] ?? []);
}
